import React from 'react';
import { Row, Col, Card, Statistic, Typography, Progress, Skeleton, Alert } from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  TableOutlined,
  WalletOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import { useResponsive } from '../../hooks/useResponsive';
import { useAppSelector } from '../../hooks/redux';
import { useGetDashboardPollingQuery } from '../../services/analyticsApi';
import { useCurrency } from '../../hooks/useCurrency';
import { DASHBOARD_QUERY, DASHBOARD_POLLING_MS } from './dashboardQuery';
import { useMoneyPrivacy, MONEY_MASK } from '../../hooks/useMoneyPrivacy';
import MoneyEye from './MoneyEye';

const { Text } = Typography;

const DashboardMetrics: React.FC = () => {
  const { isMobile } = useResponsive();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { currencySymbol } = useCurrency();
  const { isHidden, mask } = useMoneyPrivacy();

  const { data: dashboard, error, isLoading } = useGetDashboardPollingQuery(
    {
      restaurantId: currentRestaurant?.id || '',
      query: DASHBOARD_QUERY,
    },
    {
      skip: !currentRestaurant?.id,
      refetchOnMountOrArgChange: true,
      pollingInterval: DASHBOARD_POLLING_MS,
    }
  );

  if (!currentRestaurant?.id) {
    return null;
  }

  const getGrowthIcon = (growth: number) => {
    if (growth > 2) return <ArrowUpOutlined style={{ color: 'var(--color-success-text)' }} />;
    if (growth < -2) return <ArrowDownOutlined style={{ color: 'var(--color-error-text)' }} />;
    return <MinusOutlined style={{ color: 'var(--color-neutral-text)' }} />;
  };

  if (error) {
    return (
      <Alert
        message="Error al cargar métricas"
        description="No se pudieron cargar las métricas del dashboard"
        type="error"
        showIcon
        style={{ marginBottom: 24 }}
      />
    );
  }

  if (isLoading || !dashboard) {
    return (
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={8}>
          <Card>
            <Skeleton active paragraph={{ rows: 3 }} />
          </Card>
        </Col>
        {Array.from({ length: 3 }).map((_, index) => (
          <Col xs={24} sm={8} lg={index === 0 ? 6 : 5} key={index}>
            <Card>
              <Skeleton active paragraph={{ rows: 2 }} />
            </Card>
          </Col>
        ))}
      </Row>
    );
  }

  const { realTimeMetrics, financialSummary, operationalStatus } = dashboard;
  const cardStyle: React.CSSProperties = {
    borderRadius: isMobile ? 8 : 12,
    height: '100%',
  };
  const subTextStyle: React.CSSProperties = { fontSize: isMobile ? '11px' : '12px' };

  const formatMoney = (value: number) =>
    `${currencySymbol} ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const periodStrip = [
    { label: 'Semana', value: financialSummary.weekTotal },
    { label: 'Mes', value: financialSummary.monthTotal },
    { label: 'Ticket prom.', value: realTimeMetrics.averageOrderValue },
  ];

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
      {/* Ventas de hoy (hero) */}
      <Col xs={24} lg={8}>
        <Card style={cardStyle}>
          <Statistic
            title={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                Ventas de hoy
                <MoneyEye moneyKey="today" />
              </span>
            }
            // Tapado: la cifra se sustituye por la máscara, así que precision y
            // suffix se van — si no, AntD pinta "•••• .00 $".
            value={isHidden('today') ? MONEY_MASK : realTimeMetrics.todayRevenue}
            precision={isHidden('today') ? undefined : 2}
            valueStyle={{
              color: 'var(--color-success-text)',
              fontSize: isMobile ? '28px' : '34px',
              fontWeight: 'bold',
            }}
            prefix={<DollarOutlined />}
            suffix={isHidden('today') ? undefined : currencySymbol}
          />
          <div style={{ marginTop: '4px' }}>
            <Text type="secondary" style={subTextStyle}>
              {getGrowthIcon(financialSummary.dailyGrowth)} {Math.abs(financialSummary.dailyGrowth).toFixed(1)}% vs ayer a esta hora
            </Text>
          </div>
          <div
            style={{
              display: 'flex',
              gap: isMobile ? 16 : 24,
              marginTop: 14,
              paddingTop: 12,
              borderTop: '1px solid var(--border-color)',
            }}
          >
            {periodStrip.map(({ label, value }) => (
              <div key={label}>
                <Text type="secondary" style={{ display: 'block', fontSize: isMobile ? '10px' : '11px' }}>
                  {label}
                </Text>
                <Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>
                  {mask('otros', formatMoney(value))}
                </Text>
              </div>
            ))}
          </div>
        </Card>
      </Col>

      {/* Órdenes de hoy */}
      <Col xs={24} sm={8} lg={5}>
        <Card style={cardStyle}>
          <Statistic
            title="Órdenes de hoy"
            value={realTimeMetrics.todayOrderCount}
            valueStyle={{
              color: 'var(--color-info-text)',
              fontSize: isMobile ? '20px' : '24px',
              fontWeight: 'bold',
            }}
            prefix={<ShoppingCartOutlined />}
          />
          <div style={{ marginTop: '8px' }}>
            <Text type="secondary" style={subTextStyle}>
              {operationalStatus.activeOrders} activas · {operationalStatus.readyOrders} listas
            </Text>
          </div>
        </Card>
      </Col>

      {/* Efectivo en caja */}
      <Col xs={24} sm={8} lg={6}>
        <Card style={cardStyle}>
          <Statistic
            title={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                Efectivo en caja
                <MoneyEye moneyKey="cash" />
              </span>
            }
            value={isHidden('cash') ? MONEY_MASK : financialSummary.cashOnHand}
            precision={isHidden('cash') ? undefined : 2}
            valueStyle={{
              color: 'var(--color-cyan-text)',
              fontSize: isMobile ? '20px' : '24px',
              fontWeight: 'bold',
            }}
            prefix={<WalletOutlined />}
            suffix={isHidden('cash') ? undefined : currencySymbol}
          />
          <div style={{ marginTop: '8px' }}>
            <Text type="secondary" style={subTextStyle}>
              {operationalStatus.activeCashRegisters}{' '}
              {operationalStatus.activeCashRegisters === 1 ? 'caja abierta' : 'cajas abiertas'}
            </Text>
          </div>
        </Card>
      </Col>

      {/* Ocupación */}
      <Col xs={24} sm={8} lg={5}>
        <Card style={cardStyle}>
          <Statistic
            title="Ocupación"
            value={operationalStatus.occupiedTables}
            suffix={`/ ${operationalStatus.totalTables}`}
            valueStyle={{
              color: 'var(--color-reserved-text)',
              fontSize: isMobile ? '20px' : '24px',
              fontWeight: 'bold',
            }}
            prefix={<TableOutlined />}
          />
          <div style={{ marginTop: '8px' }}>
            <Progress
              percent={Math.round(operationalStatus.occupancyRate)}
              showInfo={false}
              strokeColor="var(--color-reserved-text)"
              style={{ marginBottom: '4px' }}
              size={isMobile ? 'small' : 'default'}
            />
            <Text type="secondary" style={subTextStyle}>
              {operationalStatus.occupancyRate.toFixed(1)}% ocupación
            </Text>
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default DashboardMetrics;
